import { TONES } from "../components/filters/statusColors";
import { Clipboard, ClipboardCheck, ClipboardList, ClipboardPen, type LucideIcon } from "lucide-react";
import type { TFunction } from "i18next";
import { NAV_ICONS } from "../components/icons/navIcons";
import type { ContractCaf } from "../types/contractDetail.types";

export interface ContractStatusOption {
  label: string;
  value: number;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
}

/** Mirrors ContractStatus on the backend (Emeis.Contracting.Domain.Contract) — keep numbers in sync. */
export const ContractWorkflowStatus = {
  Created: 1,
  UnderReview: 2,
  Approved: 4,
  PendingSignature: 6,
  Signed: 7,
  Active: 8,
  Expired: 9,
  SentForApproval: 12,
  SentToReviewer: 13,
  SentForExternalReview: 14,
  ExternalReviewReceived: 15,
  ReviewedInternally: 16,
  SentToRepository: 17,
} as const;

/** A contract in any of these statuses is "in someone else's court" — no edit/delete/re-send until a decision is made.
 *
 * ExternalReviewReceived is deliberately NOT here. It means the counterparty
 * has replied and the ball is back with the Editor — which is exactly when
 * they need to act on it. Locking it left the contract frozen with no way
 * forward: the reply had arrived, the status had moved, and every workflow
 * action was hidden behind a banner saying it was waiting on someone else.
 * The state machine already allows it to move on (to editing, a reviewer,
 * approval, another external round, or the repository). */
export const CONTRACT_LOCKED_STATUSES: readonly number[] = [
  ContractWorkflowStatus.SentForApproval,
  ContractWorkflowStatus.SentToReviewer,
  ContractWorkflowStatus.SentForExternalReview,
  ContractWorkflowStatus.SentToRepository,
  ContractWorkflowStatus.PendingSignature,
  ContractWorkflowStatus.Signed,
];

/** Statuses at which the document is final enough to hand out as a PDF —
 *  the contract has left drafting and is in, or headed for, the repository. */
export const CONTRACT_DOWNLOADABLE_STATUSES: readonly number[] = [
  ContractWorkflowStatus.SentToRepository,
  ContractWorkflowStatus.PendingSignature,
  ContractWorkflowStatus.Signed,
];

/**
 * A contract that has finished its workflow and is executed — the only kind
 * that can sensibly be a parent for a new one. Anything still in drafting,
 * review or approval could still change (or be abandoned), so hanging an
 * amendment or SOW off it would attach the child to terms nobody has agreed.
 *
 * Pending Signature is deliberately out: it is on its way to final, not final.
 * Expired, Terminated and Archived are out for the opposite reason — they are
 * finished but no longer live, so nothing new should be connected to them.
 */
export const CONTRACT_FINAL_STATUSES: readonly number[] = [
  ContractWorkflowStatus.Signed,
  ContractWorkflowStatus.Active,
  ContractWorkflowStatus.SentToRepository,
];

export function isContractFinal(statusVal: unknown): boolean {
  return CONTRACT_FINAL_STATUSES.includes(Number(statusVal));
}

export function isContractDownloadable(statusVal: unknown): boolean {
  return CONTRACT_DOWNLOADABLE_STATUSES.includes(Number(statusVal));
}

export function isContractLocked(statusVal: unknown): boolean {
  return CONTRACT_LOCKED_STATUSES.includes(Number(statusVal));
}

/**
 * The locked statuses where someone still owes a response — the contract is
 * sitting in a reviewer's, approver's or counterparty's queue.
 *
 * The other locked statuses (repository, pending signature, signed) are locked
 * for the opposite reason: the document is finished, and nobody is being
 * waited on. The two cases read very differently to a user, so anything
 * explaining *why* a contract is locked has to tell them apart.
 */
export const CONTRACT_AWAITING_DECISION_STATUSES: readonly number[] = [
  ContractWorkflowStatus.SentForApproval,
  ContractWorkflowStatus.SentToReviewer,
  ContractWorkflowStatus.SentForExternalReview,
];

/** What still counts as a pending task for the person it is assigned to:
 *  every locked status except the two where the work is finished (sent to
 *  the repository, signed). */
export const CONTRACT_PENDING_TASK_STATUSES: readonly number[] =
  CONTRACT_LOCKED_STATUSES.filter(
    (s) =>
      s !== ContractWorkflowStatus.SentToRepository &&
      s !== ContractWorkflowStatus.Signed,
  );

export function isAwaitingDecision(statusVal: unknown): boolean {
  return CONTRACT_AWAITING_DECISION_STATUSES.includes(Number(statusVal));
}

/**
 * Statuses a contract passes through BEFORE its CAF has ever gone to an
 * Approver. Written as a whitelist of the pre-approval states rather than a
 * list of post-approval ones, so a status added later defaults to "has been
 * seen by the Approver" — the safe direction for the CAF clause guard, which
 * relies on this.
 */
const PRE_APPROVAL_STATUSES: readonly number[] = [
  1, // Created
  ContractWorkflowStatus.UnderReview,
  ContractWorkflowStatus.SentToReviewer,
  ContractWorkflowStatus.SentForExternalReview,
  ContractWorkflowStatus.ExternalReviewReceived,
  ContractWorkflowStatus.ReviewedInternally,
];

/**
 * True once the contract has been sent for approval at least once — including
 * after it comes back Approved or Rejected, since by then the Approver has
 * already seen the CAF's clauses. Mirrors UpdateCafCommandHandler.
 */
export function hasGoneToApprover(statusVal: unknown): boolean {
  const n = Number(statusVal);
  if (!Number.isFinite(n) || n === 0) return false;
  return !PRE_APPROVAL_STATUSES.includes(n);
}

/** CAF-specific freeze: standard CLM behavior is to permanently freeze the
 * approval form once the contract has been formally Approved or Signed —
 * and ONLY those two, deliberately NOT the broader isContractLocked() set.
 * SentForApproval is itself one of isContractLocked's statuses (it's "in
 * someone else's court" for general edits), but that's precisely the stage
 * the Approver needs to actively sign off clauses and write remarks on the
 * CAF — folding isContractLocked in here would freeze the form during the
 * one phase it's actually meant to be used. */
export function isCafClosedForStatus(statusVal: unknown): boolean {
  return (
    Number(statusVal) === ContractWorkflowStatus.Approved ||
    Number(statusVal) === ContractWorkflowStatus.Signed
  );
}

/** Content freeze: once a contract has been Approved, its terms are what the
 * Approvers actually signed off, so the Editor can no longer change them —
 * otherwise what gets signed is not what was approved, with nothing to show
 * the difference. Covers everything from Approved onward through the executed
 * lifecycle.
 *
 * Deliberately separate from isContractLocked(), which means "in someone
 * else's court, pending a decision" and carries its own banner wording. A
 * contract sitting at Approved is in nobody's court — it is simply settled,
 * and that is a different reason for the same restriction.
 *
 * Rejected is NOT included: a rejected contract goes back to the Editor
 * precisely so it can be corrected and re-sent.
 */
export function isContentFrozenForStatus(statusVal: unknown): boolean {
  const n = Number(statusVal);
  return (
    n === ContractWorkflowStatus.Approved ||
    n === ContractWorkflowStatus.PendingSignature ||
    n === ContractWorkflowStatus.Signed ||
    n === ContractWorkflowStatus.Active ||
    n === ContractWorkflowStatus.Expired ||
    n === ContractWorkflowStatus.SentToRepository ||
    n === 10 || // Terminated
    n === 11 //   Archived
  );
}

/** Mirrors DecisionType on the backend (Emeis.Contracting.Domain.DecisionType). */
export const DecisionType = {
  Approved: 1,
  ApprovedWithComments: 2,
  ChangesRequired: 3,
  NotApproved: 4,
} as const;

export function getDecisionTypeLabel(
  decisionType: unknown,
  t: TFunction,
): string {
  switch (Number(decisionType)) {
    case DecisionType.Approved:
      return t("detail.workflow.decisionApproved", {
        defaultValue: "Approved",
      });
    case DecisionType.ApprovedWithComments:
      return t("detail.workflow.decisionApprovedWithComments", {
        defaultValue: "Approved with Comments",
      });
    case DecisionType.ChangesRequired:
      return t("detail.workflow.decisionChangesRequired", {
        defaultValue: "Changes Required",
      });
    case DecisionType.NotApproved:
      return t("detail.workflow.decisionNotApproved", {
        defaultValue: "Not Approved",
      });
    default:
      return "-";
  }
}

export function getDecisionTypeBadgeClass(decisionType: unknown): string {
  switch (Number(decisionType)) {
    case DecisionType.Approved:
    case DecisionType.ApprovedWithComments:
      return `${TONES.success.bg} ${TONES.success.text}`;
    case DecisionType.ChangesRequired:
      return `${TONES.attention.bg} ${TONES.attention.text}`;
    case DecisionType.NotApproved:
      return `${TONES.danger.bg} ${TONES.danger.text}`;
    default:
      return `${TONES.neutral.bg} ${TONES.neutral.text}`;
  }
}

/** Shared across the listing, Quick View, and Detail page so the status
 * switch statement isn't duplicated three times. */
export function getContractStatusLabel(
  statusVal: unknown,
  t: TFunction,
): string {
  switch (Number(statusVal)) {
    case 1:
      return t("statusOptions.created", { defaultValue: "Created" });
    case 2:
      // "Under Review" is misleading here — this status means the contract
      // is with the Editor actively editing it (set by SendToEditor), not
      // that a Reviewer has it. Matches the workflow diagram's IN_EDITING.
      return t("statusOptions.inEditing", { defaultValue: "In Editing" });
    case 3:
      return t("statusOptions.waitingApproval", {
        defaultValue: "Waiting Approval",
      });
    case 4:
      return t("statusOptions.approved", { defaultValue: "Approved" });
    case 5:
      return t("statusOptions.rejected", { defaultValue: "Rejected" });
    case 6:
      return t("statusOptions.pendingSignature", {
        defaultValue: "Pending Signature",
      });
    case 7:
      return t("statusOptions.signed", { defaultValue: "Signed" });
    case 8:
      return t("statusOptions.active", { defaultValue: "Active" });
    case 9:
      return t("statusOptions.expired", { defaultValue: "Expired" });
    case 10:
      return t("statusOptions.terminated", { defaultValue: "Terminated" });
    case 11:
      return t("statusOptions.archived", { defaultValue: "Archived" });
    case 12:
      return t("statusOptions.sentForApproval", {
        defaultValue: "Sent For Approval",
      });
    case 13:
      return t("statusOptions.sentToReviewer", {
        defaultValue: "Sent To Reviewer",
      });
    case 14:
      return t("statusOptions.sentForExternalReview", {
        defaultValue: "Sent For External Review",
      });
    case 15:
      return t("statusOptions.externalReviewReceived", {
        defaultValue: "External Review Received",
      });
    case 16:
      return t("statusOptions.reviewedInternally", {
        defaultValue: "Reviewed",
      });
    case 17:
      return t("statusOptions.sentToRepository", {
        defaultValue: "Sent To Repository",
      });
    default:
      return t("statusOptions.created", { defaultValue: "Created" });
  }
}

export function getContractStatusEnglishLabel(statusVal: unknown): string {
  switch (Number(statusVal)) {
    case 1:
      return "Created";
    case 2:
      return "In Editing";
    case 3:
      return "Waiting Approval";
    case 4:
      return "Approved";
    case 5:
      return "Rejected";
    case 6:
      return "Pending Signature";
    case 7:
      return "Signed";
    case 8:
      return "Active";
    case 9:
      return "Expired";
    case 10:
      return "Terminated";
    case 11:
      return "Archived";
    case 12:
      return "Sent For Approval";
    case 13:
      return "Sent To Reviewer";
    case 14:
      return "Sent For External Review";
    case 15:
      return "External Review Received";
    case 16:
      return "Reviewed";
    case 17:
      return "Sent To Repository";
    default:
      return "Created";
  }
}

/** Mirrors DocumentSource on the backend (Emeis.Contracting.Domain.DocumentSource). */
export const DocumentSource = {
  Initiator: 1,
  Editor: 2,
  ExternalReviewer: 3,
  Template: 4,
  FinalVersion: 5,
} as const;

export function getDocumentSourceLabel(source: unknown, t: TFunction): string {
  switch (Number(source)) {
    case DocumentSource.Initiator:
      return t("documents.sourceInitiator", { defaultValue: "Initiator" });
    case DocumentSource.Editor:
      return t("documents.sourceEditor", { defaultValue: "Editor" });
    case DocumentSource.ExternalReviewer:
      return t("documents.sourceExternalReviewer", {
        defaultValue: "External Reviewer",
      });
    case DocumentSource.Template:
      return t("documents.sourceTemplate", { defaultValue: "Template" });
    case DocumentSource.FinalVersion:
      return t("documents.sourceFinalVersion", {
        defaultValue: "Final (Signed)",
      });
    default:
      return "-";
  }
}

/** Mirrors ContractParticipantRole/ContractParticipantStatus on the backend (Emeis.Contracting.Domain). */
export const ContractParticipantRole = { Reviewer: 1, Approver: 2 } as const;
export const ContractParticipantStatus = {
  Pending: 1,
  InProgress: 2,
  Completed: 3,
  Returned: 4,
  Skipped: 5,
  Cancelled: 6,
} as const;

export function getParticipantStatusLabel(
  status: unknown,
  t: TFunction,
): string {
  switch (Number(status)) {
    case ContractParticipantStatus.Pending:
      return t("participants.pending", { defaultValue: "Pending" });
    case ContractParticipantStatus.InProgress:
      return t("participants.inProgress", { defaultValue: "In Progress" });
    case ContractParticipantStatus.Completed:
      return t("participants.completed", { defaultValue: "Completed" });
    case ContractParticipantStatus.Returned:
      return t("participants.returned", { defaultValue: "Returned" });
    case ContractParticipantStatus.Skipped:
      return t("participants.skipped", { defaultValue: "Skipped" });
    case ContractParticipantStatus.Cancelled:
      return t("participants.cancelled", { defaultValue: "Cancelled" });
    default:
      return "-";
  }
}

/** Tailwind bg/text classes for a participant status badge — every status
 * gets its own fill so the chip is never just the same color as the card
 * it sits on (which read as "no background" when only Completed was colored). */
export function getParticipantStatusBadgeClass(status: unknown): string {
  switch (Number(status)) {
    case ContractParticipantStatus.Completed:
      return `${TONES.success.bg} ${TONES.success.text}`;
    case ContractParticipantStatus.InProgress:
      return `${TONES.info.bg} ${TONES.info.text}`;
    case ContractParticipantStatus.Returned:
      return `${TONES.attention.bg} ${TONES.attention.text}`;
    case ContractParticipantStatus.Cancelled:
      return `${TONES.danger.bg} ${TONES.danger.text}`;
    case ContractParticipantStatus.Skipped:
      return `${TONES.neutral.bg} ${TONES.neutral.text}`;
    case ContractParticipantStatus.Pending:
    default:
      return `${TONES.neutral.bg} ${TONES.neutral.text}`;
  }
}

/** Mirrors ContractCommentStatus on the backend (Emeis.Contracting.Domain.ContractCommentStatus). */
export const ContractCommentStatus = {
  Unresolved: 1,
  Resolved: 2,
  WontFix: 3,
} as const;

export function getContractCommentStatusLabel(
  status: unknown,
  t: TFunction,
): string {
  switch (Number(status)) {
    case ContractCommentStatus.Resolved:
      return t("comments.resolved", { defaultValue: "Resolved" });
    case ContractCommentStatus.WontFix:
      return t("comments.wontFix", { defaultValue: "Won't Fix" });
    case ContractCommentStatus.Unresolved:
    default:
      return t("comments.unresolved", { defaultValue: "Unresolved" });
  }
}

export function getContractCommentStatusBadgeClass(status: unknown): string {
  switch (Number(status)) {
    case ContractCommentStatus.Resolved:
      return `${TONES.success.bg} ${TONES.success.text}`;
    case ContractCommentStatus.WontFix:
      return `${TONES.neutral.bg} ${TONES.neutral.text}`;
    case ContractCommentStatus.Unresolved:
    default:
      return `${TONES.attention.bg} ${TONES.attention.text}`;
  }
}

/** Mirrors CafClauseApprovalStatus on the backend (Emeis.Contracting.Domain.Contract). */
export const CafClauseApprovalStatus = {
  Pending: 1,
  Approved: 2,
  ChangesRequested: 3,
} as const;

export function getCafClauseApprovalStatusLabel(
  status: unknown,
  t: TFunction,
): string {
  switch (Number(status)) {
    case CafClauseApprovalStatus.Approved:
      return t("cafTab.clauseApproved", { defaultValue: "Approved" });
    case CafClauseApprovalStatus.ChangesRequested:
      return t("cafTab.clauseChangesRequested", {
        defaultValue: "Changes Requested",
      });
    case CafClauseApprovalStatus.Pending:
    default:
      return t("cafTab.clausePending", { defaultValue: "Pending" });
  }
}

export function getCafClauseApprovalStatusBadgeClass(status: unknown): string {
  switch (Number(status)) {
    case CafClauseApprovalStatus.Approved:
      return `${TONES.success.bg} ${TONES.success.text}`;
    case CafClauseApprovalStatus.ChangesRequested:
      return `${TONES.danger.bg} ${TONES.danger.text}`;
    case CafClauseApprovalStatus.Pending:
    default:
      return `${TONES.attention.bg} ${TONES.attention.text}`;
  }
}

/** The CAF status badge's four states, shared between the Contract Detail
 * header badge and the All Contracts listing filter so both agree on what
 * "initiated"/"complete"/"pending" means for a given CAF. */
export const CafBadgeState = {
  NotInitiated: "not_initiated",
  Incomplete: "incomplete",
  Pending: "pending",
  Complete: "complete",
} as const;
export type CafBadgeStateValue =
  (typeof CafBadgeState)[keyof typeof CafBadgeState];

/** Derives the CAF badge state from a fetched CAF (or null, for a contract
 * whose CAF was never opened/saved). Mirrors the initiated/complete/
 * allApproved logic previously inlined in ContractDetail.tsx. */
export function getCafBadgeState(
  caf: ContractCaf | null | undefined,
): CafBadgeStateValue {
  const initiated =
    !!caf &&
    (!!caf.firstParty ||
      !!caf.secondParty ||
      !!caf.purpose ||
      caf.clauses.length > 0);

  if (!initiated) return CafBadgeState.NotInitiated;

  const complete =
    !!caf.firstParty &&
    !!caf.secondParty &&
    !!caf.purpose &&
    caf.clauses.length > 0 &&
    caf.clauses.every((c) => !!c.clauseNo && !!c.approver);

  if (!complete) return CafBadgeState.Incomplete;

  const allApproved =
    caf.clauses.length === 0 ||
    caf.clauses.every(
      (c) => c.approvalStatus === CafClauseApprovalStatus.Approved,
    );

  return allApproved ? CafBadgeState.Complete : CafBadgeState.Pending;
}

export function getCafBadgeStateLabel(
  state: CafBadgeStateValue,
  t: TFunction,
): string {
  switch (state) {
    case CafBadgeState.Complete:
      return t("detail.cafComplete", { defaultValue: "CAF Complete" });
    case CafBadgeState.Pending:
      return t("detail.cafPendingBadge", { defaultValue: "CAF Pending" });
    case CafBadgeState.Incomplete:
      return t("detail.cafIncompleteBadge", { defaultValue: "CAF Incomplete" });
    case CafBadgeState.NotInitiated:
    default:
      return t("filters.cafNotInitiated", { defaultValue: "Not Initiated" });
  }
}

export function getCafBadgeStateBadgeClass(state: CafBadgeStateValue): string {
  switch (state) {
    case CafBadgeState.Complete:
      return `${TONES.success.bg} ${TONES.success.text}`;
    case CafBadgeState.Pending:
      return `${TONES.info.bg} ${TONES.info.text}`;
    case CafBadgeState.Incomplete:
      return `${TONES.attention.bg} ${TONES.attention.text}`;
    case CafBadgeState.NotInitiated:
    default:
      return `${TONES.neutral.bg} ${TONES.neutral.text}`;
  }
}

/** Icon for the CAF state pill, matching its tone. */
export function getCafBadgeStateIcon(state: CafBadgeStateValue): LucideIcon {
  switch (state) {
    case CafBadgeState.Complete:
      return ClipboardCheck;
    case CafBadgeState.Pending:
      return ClipboardList;
    case CafBadgeState.Incomplete:
      return ClipboardPen;
    default:
      return Clipboard;
  }
}

/** Options for the CAF status filter dropdown on the All Contracts listing. */
export function getCafBadgeStateOptions(
  t: TFunction,
): { label: string; value: CafBadgeStateValue }[] {
  return [
    CafBadgeState.NotInitiated,
    CafBadgeState.Incomplete,
    CafBadgeState.Pending,
    CafBadgeState.Complete,
  ].map((value) => ({ value, label: getCafBadgeStateLabel(value, t) }));
}

export function getContractStatusOptions(t: TFunction): ContractStatusOption[] {
  const clock = NAV_ICONS.clock || NAV_ICONS.tick;
  return [
    {
      label: t("statusOptions.created", { defaultValue: "Created" }),
      value: 1,
      icon: clock,
    },
    {
      label: t("statusOptions.inEditing", { defaultValue: "In Editing" }),
      value: 2,
      icon: clock,
    },
    {
      label: t("statusOptions.waitingApproval", {
        defaultValue: "Waiting Approval",
      }),
      value: 3,
      icon: clock,
    },
    {
      label: t("statusOptions.approved", { defaultValue: "Approved" }),
      value: 4,
      icon: NAV_ICONS.tick,
    },
    {
      label: t("statusOptions.rejected", { defaultValue: "Rejected" }),
      value: 5,
      icon: NAV_ICONS.close,
    },
    {
      label: t("statusOptions.pendingSignature", {
        defaultValue: "Pending Signature",
      }),
      value: 6,
      icon: clock,
    },
    {
      label: t("statusOptions.signed", { defaultValue: "Signed" }),
      value: 7,
      icon: NAV_ICONS.tick,
    },
    {
      label: t("statusOptions.active", { defaultValue: "Active" }),
      value: 8,
      icon: NAV_ICONS.tick,
    },
    {
      label: t("statusOptions.expired", { defaultValue: "Expired" }),
      value: 9,
      icon: NAV_ICONS.close,
    },
    {
      label: t("statusOptions.terminated", { defaultValue: "Terminated" }),
      value: 10,
      icon: NAV_ICONS.close,
    },
    {
      label: t("statusOptions.archived", { defaultValue: "Archived" }),
      value: 11,
      icon: NAV_ICONS.close,
    },
    {
      label: t("statusOptions.sentForApproval", {
        defaultValue: "Sent For Approval",
      }),
      value: 12,
      icon: clock,
    },
    {
      label: t("statusOptions.sentToReviewer", {
        defaultValue: "Sent To Reviewer",
      }),
      value: 13,
      icon: clock,
    },
    {
      label: t("statusOptions.sentForExternalReview", {
        defaultValue: "Sent For External Review",
      }),
      value: 14,
      icon: clock,
    },
    {
      label: t("statusOptions.externalReviewReceived", {
        defaultValue: "External Review Received",
      }),
      value: 15,
      icon: clock,
    },
    {
      label: t("statusOptions.reviewedInternally", {
        defaultValue: "Reviewed",
      }),
      value: 16,
      icon: clock,
    },
    {
      label: t("statusOptions.sentToRepository", {
        defaultValue: "Sent To Repository",
      }),
      value: 17,
      icon: NAV_ICONS.tick,
    },
  ];
}
